import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Package, DollarSign, CheckCircle2, Clock, TrendingUp } from "lucide-react";
import api from "@/lib/api";

/**
 * v2.7.58+E37 · DELIVERY SYSTEM · Section 5
 *
 * Compact rollup of delivery-specific payout totals shown on the Driver
 * Earnings page. Pulls from GET /api/deliveries/mine/earnings/rollup — a
 * dedicated endpoint that iterates the `delivery_payouts` ledger populated
 * by the auto-completion trigger in driver_mark_delivered().
 *
 * "Pending" = ledger row POSTED but not yet PAID_OUT (admin hasn't marked
 * it settled in the payout ledger console). "Paid Out" = ops has confirmed
 * the physical transfer via /admin/payouts/{id}/settle.
 */
export default function DeliveryEarningsSection() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/deliveries/mine/earnings/rollup");
      setData(r.data);
    } catch { /* silent — new drivers with no deliveries just see zeroes */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  if (!data) return null;
  const hasAny = (data.all_time?.count || 0) > 0;

  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid="delivery-earnings-section">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center">
            <Package className="h-4 w-4 text-emerald-300"/>
          </div>
          <div>
            <div className="text-sm font-bold text-zinc-100">Delivery Earnings</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Multi-stop manifests · payout ledger</div>
          </div>
        </div>
        {!hasAny && (
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">No completed deliveries yet</div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="delivery-earnings-grid">
        {[
          { key: "today",    label: "Today"    },
          { key: "week",     label: "Week"     },
          { key: "all_time", label: "All Time" },
        ].map((b) => {
          const v = data[b.key] || {};
          return (
            <div
              key={b.key}
              className="bg-zinc-950 border border-zinc-800 rounded p-3"
              data-testid={`delivery-earnings-${b.key}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">{b.label}</div>
              <div className="mt-1 text-xl font-extrabold text-emerald-300 meter-display">
                ${(v.gross || 0).toFixed(2)}
              </div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                {v.count || 0} manifest{v.count === 1 ? "" : "s"} · ${(v.tip || 0).toFixed(2)} tips
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-[10px]">
                <div className="flex items-center gap-1 text-emerald-300" data-testid={`delivery-earnings-${b.key}-paid`}>
                  <CheckCircle2 className="h-3 w-3"/>
                  Paid <span className="font-bold ml-0.5">${(v.paid_out || 0).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-1 text-amber-300" data-testid={`delivery-earnings-${b.key}-pending`}>
                  <Clock className="h-3 w-3"/>
                  Pending <span className="font-bold ml-0.5">${(v.pending || 0).toFixed(2)}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-1.5 text-[10px] text-zinc-500">
        <TrendingUp className="h-3 w-3"/>
        Pending totals move to Paid once the office marks the payout as settled in the ledger.
      </div>
    </Card>
  );
}
